import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  styled,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CircleIcon from "@mui/icons-material/Circle";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { Loader } from "../../Components/Loader";
import { Feedback } from "../../Components/Feedback";

const LeftContent = styled("div")(({ theme }) => ({
  "& .rfp-section": {
    padding: theme.spacing(0.75, 1),
    borderRadius: theme.shape.borderRadius,
    transition: "background-color 160ms ease, box-shadow 160ms ease",
  },

  "& .rfp-section.rfp-section--active": {
    backgroundColor: "#80b4e447",
    boxShadow: `0 0 ${theme.palette.primary.light}`,
  },

  // Optional: make links look nice inside the HTML
  "& a": {
    color: theme.palette.primary.main,
    textDecoration: "underline",
  },
}));

const HoverListItem = styled(ListItem)(({ theme }) => ({
  "&:hover": {
    paddingTop: "10px",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
    cursor: "pointer",
  },
}));

const StepOneScreen = ({
  company,
  NextStep,
  rfpFile,
  setRfpFile,
  uploadCount,
  setUploadCount,
  additionalInfoSections,
  setAdditionalInfoSections,
  resummarizeCount,
  onResummarize,
  isLoading,
  apiResponse,
  insuranceType,
  error,
  BASE_URL, // ✅ added (only for feedback API)
}) => {
  const [additionalInfoOpen, setAdditionalInfoOpen] = useState(false);
  const [localSections, setLocalSections] = useState([]);
  const [pdfUrl, setPdfUrl] = useState();
  //const apiResponse = response;
  const rfpSummaryMetadata = apiResponse?.rfp_summary?.ai_summarized_metadata;
  const documentTitle =
    apiResponse?.rfp_summary?.rfp_document?.split("RFP)")[0] + "RFP)**";
  const [showEmail, setShowEmail] = useState(false);
  const [emailText, setEmailText] = useState(
    apiResponse?.completeness?.outreach_email
  );
  const missingItems = apiResponse?.completeness?.rfp_completeness_items
    ?.filter((item) => item.status !== "YES")
    .flatMap((item) => item.action_needed);

  // derive upload flags from lifted state
  const isFirstUpload = uploadCount === 1;
  const hasReplacementUpload = uploadCount > 1;

  // when dialog opens, sync local copy from parent state
  useEffect(() => {
    if (additionalInfoOpen) {
      if (additionalInfoSections && additionalInfoSections.length > 0) {
        setLocalSections(additionalInfoSections);
      } else {
        setLocalSections([]);
      }
    }
  }, [additionalInfoOpen, additionalInfoSections]);

  function normalizeMarkdown(md) {
    console.log(JSON.stringify(md));
    return md.replace(/\\n/g, "\n").replace(/\r\n/g, "\n");
  }

  const handleFileChange = async (event) => {
    setShowEmail(false);
    const selected = event.target.files?.[0];
    if (selected) {
      setRfpFile(selected);
      setUploadCount((prev) => prev + 1);

      const url = URL.createObjectURL(selected);
      setPdfUrl(url);
    }
  };

  const handleApproveClick = () => {
    // First-upload behavior: send mail + stay on same step
    alert("Mail sent. Awaiting missing information.");
  };

  const handleContinueClick = () => {
    // Replacement behavior: no mail, just move to next step
    NextStep();
  };

  // -------- Additional Info Dialog handlers --------
  const handleOpenAdditionalInfo = () => {
    setAdditionalInfoOpen(true);
  };

  const handleCloseAdditionalInfo = () => {
    setAdditionalInfoOpen(false);
  };

  const handleAddSection = () => {
    setLocalSections((prev) => [
      ...prev,
      {
        id: Date.now(),
        description: "",
        file: null,
      },
    ]);
  };

  const handleRemoveSection = (id) => {
    setLocalSections((prev) => prev.filter((s) => s.id !== id));
  };

  const handleSectionTextChange = (id, value) => {
    setLocalSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, description: value } : s))
    );
  };

  const handleSectionFileChange = (id, event) => {
    const file = event.target.files?.[0] || null;
    setLocalSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, file } : s))
    );
  };

  const handleSaveAndResummarize = () => {
    setAdditionalInfoSections(localSections);
    onResummarize && onResummarize();
    setAdditionalInfoOpen(false);
  };

  function toggleSection(sectionKey, on) {
    const el = document.querySelector(
      `#left-content .rfp-section[data-section="${sectionKey}"]`
    );
    if (!el) return;

    el.classList.toggle("rfp-section--active", on);

    if (on) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  // chip label changes slightly if re-summarized
  const summaryChipLabel = hasReplacementUpload
    ? "Summary updated with latest document"
    : resummarizeCount > 0
      ? "Summary refreshed with additional information"
      : "Summarizing the document...";

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        py: 3,
        px: 2,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box sx={{ width: "95vw", maxWidth: 1500 }}>
        {/* Top: Opportunity header + upload + additional info button */}
        <Card
          sx={{
            mb: 3,
            borderRadius: 3,
          }}
        >
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {company} #482
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {insuranceType} Insurance – RFP
                </Typography>
              </Box>

              {/* Upload Area + Additional Info */}
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems="flex-start"
              >
                <Box>
                  <input
                    accept=".msg"
                    id="rfp-upload"
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                  <label htmlFor="rfp-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadFileIcon />}
                      sx={{ borderRadius: 999 }}
                    >
                      {rfpFile ? "Replace RFP Document" : "Upload RFP Document"}
                    </Button>
                  </label>
                  {rfpFile && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      Uploaded: {rfpFile.name}
                    </Typography>
                  )}
                </Box>

                <Button
                  variant="contained" // filled navy
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAdditionalInfo}
                  sx={{ borderRadius: 999, whiteSpace: "nowrap" }}
                  disabled={!rfpFile} // disabled until RFP uploaded
                >
                  Add Additional Information
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
        {isLoading && (
          <Loader text={"Summarizing the document..."} error={error} />
        )}

        {/* BELOW: visible only after file is uploaded */}
        {apiResponse && !isLoading && (
          <>
            <Stack spacing={3}>
              {/* 1) Document + Summary split */}
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={3}
                sx={{ alignItems: "stretch" }}
              >
                {/* Left: RFP Document (fixed height, internal scroll) */}
                <Card sx={{ flex: 2, borderRadius: 3 }}>
                  <CardContent
                    sx={{
                      height: 450,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 2 }}
                    >
                      <UploadFileIcon fontSize="small" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        RFP Document
                      </Typography>
                    </Stack>

                    <Box
                      sx={{
                        flex: 1,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        overflowY: "auto",
                        p: 3,
                        bgcolor: "background.paper",
                      }}
                    >
                      <LeftContent
                        id="left-content"
                        dangerouslySetInnerHTML={{
                          __html: apiResponse?.rfp_summary?.rfp_document_html,
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>

                {/* Right: Summary */}
                <Card sx={{ flex: 1.4, borderRadius: 3 }}>
                  <CardContent
                    sx={{
                      height: 450,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Summary heading + AI chip */}
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        RFP Summary
                      </Typography>
                      <AIReviewedChip />
                    </Stack>

                    <Box sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
                      <List dense sx={{ pl: 1 }}>
                        <HoverListItem
                          disableGutters
                          sx={{ alignItems: "flex-start", cursor: "pointer" }}
                          onMouseEnter={() =>
                            toggleSection("EMPLOYER_GROUP_INFORMATION", true)
                          }
                          onMouseLeave={() =>
                            toggleSection("EMPLOYER_GROUP_INFORMATION", false)
                          }
                        >
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            <CircleIcon sx={{ fontSize: 6 }} />
                          </ListItemIcon>
                          <ListItemText
                            sx={{ mt: -1 }}
                            primary={`Employer: ${
                              rfpSummaryMetadata?.employer_name ?? "NA"
                              }`}
                          />
                        </HoverListItem>

                        <HoverListItem
                          disableGutters
                          sx={{ alignItems: "flex-start", cursor: "pointer" }}
                          onMouseEnter={() =>
                            toggleSection("BROKER_GA_INFORMATION", true)
                          }
                          onMouseLeave={() =>
                            toggleSection("BROKER_GA_INFORMATION", false)
                          }
                        >
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            <CircleIcon sx={{ fontSize: 6 }} />
                          </ListItemIcon>
                          <ListItemText
                            sx={{ mt: -1 }}
                            primary={`Broker: ${
                              rfpSummaryMetadata?.broker_ga ?? "NA"
                              }`}
                          />
                        </HoverListItem>

                        <HoverListItem
                          disableGutters
                          sx={{ alignItems: "flex-start", cursor: "pointer" }}
                          onMouseEnter={() => toggleSection("KEY_DATES", true)}
                          onMouseLeave={() => toggleSection("KEY_DATES", false)}
                        >
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            <CircleIcon sx={{ fontSize: 6 }} />
                          </ListItemIcon>
                          <ListItemText
                            sx={{ mt: -1 }}
                            primary={`Effective Date: ${
                              rfpSummaryMetadata?.effective_date ?? "NA"
                              }`}
                          />
                        </HoverListItem>

                        <HoverListItem
                          disableGutters
                          sx={{ alignItems: "flex-start", cursor: "pointer" }}
                          onMouseEnter={() =>
                            toggleSection("ELIGIBILITY_PARTICIPATION", true)
                          }
                          onMouseLeave={() =>
                            toggleSection("ELIGIBILITY_PARTICIPATION", false)
                          }
                        >
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            <CircleIcon sx={{ fontSize: 6 }} />
                          </ListItemIcon>
                          <ListItemText
                            sx={{ mt: -1 }}
                            primary={`Group Size: ${
                              rfpSummaryMetadata?.group_size ?? "NA"
                              }  `}
                          />
                        </HoverListItem>

                        <HoverListItem
                          disableGutters
                          sx={{ alignItems: "flex-start", cursor: "pointer" }}
                          onMouseEnter={() =>
                            toggleSection("EMPLOYER_GROUP_INFORMATION", true)
                          }
                          onMouseLeave={() =>
                            toggleSection("EMPLOYER_GROUP_INFORMATION", false)
                          }
                        >
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            <CircleIcon sx={{ fontSize: 6 }} />
                          </ListItemIcon>
                          <ListItemText
                            sx={{ mt: -1 }}
                            primary={`Industry: ${
                              rfpSummaryMetadata?.other_key_fields?.industries ??
                              "NA"
                              }`}
                          />
                        </HoverListItem>

                        <HoverListItem
                          disableGutters
                          sx={{ alignItems: "flex-start", cursor: "pointer" }}
                          onMouseEnter={() =>
                            toggleSection("REQUESTED_COVERAGES", true)
                          }
                          onMouseLeave={() =>
                            toggleSection("REQUESTED_COVERAGES", false)
                          }
                        >
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            <CircleIcon sx={{ fontSize: 6 }} />
                          </ListItemIcon>
                          <ListItemText
                            sx={{ mt: -1 }}
                            primary="Coverages requested:"
                            secondary={
                              <>
                                {rfpSummaryMetadata?.coverage_requested?.map(
                                  (item) => (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mx: 0.75 }}
                                    >
                                    - {item}
                                    </Typography>
                                  )
                                )}
                              </>
                            }
                          />
                        </HoverListItem>

                        <HoverListItem
                          disableGutters
                          sx={{ alignItems: "flex-start", cursor: "pointer" }}
                          onMouseEnter={() =>
                            toggleSection("SPECIAL_REQUESTS_NOTES", true)
                          }
                          onMouseLeave={() =>
                            toggleSection("SPECIAL_REQUESTS_NOTES", false)
                          }
                        >
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            <CircleIcon sx={{ fontSize: 6 }} />
                          </ListItemIcon>
                          <ListItemText
                            sx={{ mt: -1 }}
                            primary={`Additional notes: ${rfpSummaryMetadata?.notes ?? "NA"}`}
                          />
                        </HoverListItem>
                      </List>
                    </Box>

                    {/* ✅ Feedback: RFP Summary */}
                    <Feedback
                      rfpFile={rfpFile}
                      BASE_URL={BASE_URL}
                      field_path="rfp_summary.ai_summarized_metadata"
                    />
                  </CardContent>
                </Card>
              </Stack>

              {/* 2) AI Summarized Metadata */}
              <Card
                sx={{
                  borderRadius: 3,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent sx={{ display: "flex", flexDirection: "column" }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <CheckCircleIcon color="primary" fontSize="small" />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      AI Explainability
                    </Typography>
                    {/* AI chip */}
                    <AIReviewedChip />
                  </Stack>

                  <Box
                    sx={{
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "primary.light",
                      bgcolor: "rgba(5,102,192,0.05)",
                      p: 2,
                      fontSize: "0.85rem",
                    }}
                  >
                    <Markdown breaks>
                      {apiResponse?.rfp_summary?.summary}
                    </Markdown>
                  </Box>

                  {/* ✅ Feedback: AI Explainability */}
                  <Feedback
                    rfpFile={rfpFile}
                    BASE_URL={BASE_URL}
                    field_path="rfp_summary.summary"
                  />
                </CardContent>
              </Card>

              <Card
                sx={{
                  borderRadius: 3,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent sx={{ display: "flex", flexDirection: "column" }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <CheckCircleIcon color="primary" fontSize="small" />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Broker Experience
                    </Typography>
                    {/* AI chip */}
                    <AIReviewedChip />
                  </Stack>

                  <Box
                    sx={{
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "primary.light",
                      bgcolor: "rgba(5,102,192,0.05)",
                      p: 2,
                      fontSize: "0.85rem",
                    }}
                  >
                    <Markdown breaks>
                    {/* The broker has indicated a high level of urgency due to the effective date being close and has explicitly requested fast-tracking of the proposal. */}
                      {apiResponse?.rfp_summary?.broker_experience}
                    </Markdown>
                  </Box>

                  {/* ✅ Feedback: AI Explainability */}
                  <Feedback
                    rfpFile={rfpFile}
                    BASE_URL={BASE_URL}
                    field_path="rfp_summary.broker_experience"
                  />
                </CardContent>
              </Card>

              {/* 3) Missing Information Detection */}
              <Card
                sx={{
                  borderRadius: 3,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent sx={{ display: "flex", flexDirection: "column" }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    {missingItems?.length === 0 ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <WarningAmberIcon color="error" fontSize="small" />
                    )}
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Missing Information
                    </Typography>
                    {/* AI chip */}
                    <AIReviewedChip />
                  </Stack>

                  {missingItems?.length === 0 ? (
                    <>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ m: 1 }}
                      >
                        No missing fields. All required Life underwriting
                        information is present in the RFP and any supplemental
                        responses.
                      </Typography>

                      {/* ✅ Feedback: Missing Info (even if none) */}
                      <Feedback
                        rfpFile={rfpFile}
                        BASE_URL={BASE_URL}
                        field_path="completeness.rfp_completeness_items.action_needed"
                      />
                    </>
                  ) : (
                    <>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        AI detects missing fields based on Life underwriting
                        requirements. The following information is required to
                        proceed with the quote but was not found in the RFP
                        document:
                      </Typography>

                      <Box
                        sx={{
                          borderRadius: 2,
                          bgcolor: "action.hover",
                          p: 2,
                        }}
                      >
                        {missingItems?.map((item) => (
                          <Typography sx={{ mt: -1, mb: 1 }} variant="body2">
                            <CircleIcon
                              sx={{ color: "error.main", fontSize: 8, mr: 1 }}
                            />
                            {item}
                          </Typography>
                        ))}
                      </Box>

                      {/* ✅ Feedback: Missing Information */}
                      <Feedback
                        rfpFile={rfpFile}
                        BASE_URL={BASE_URL}
                        field_path="completeness.rfp_completeness_items.action_needed"
                      />

                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        spacing={1.5}
                        sx={{ mt: 2 }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => setShowEmail(true)}
                        >
                          Draft Email
                        </Button>
                      </Stack>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* 4) AI-Generated Outreach Email & Action Button */}
              {showEmail && (
                <Card sx={{ borderRadius: 3, mb: 4 }}>
                  <CardContent sx={{ display: "flex", flexDirection: "column" }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1.5 }}
                    >
                      <MailOutlineIcon color="primary" fontSize="small" />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        AI-Generated Outreach Email
                      </Typography>
                      {/* AI chip */}
                      <AIReviewedChip />
                    </Stack>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      You may edit or refine the email directly in the input
                      field below.
                    </Typography>

                    <TextField
                      multiline
                      fullWidth
                      minRows={10}
                      maxRows={14}
                      defaultValue={apiResponse?.completeness?.outreach_email}
                      value={emailText}
                      onChange={(e) => setEmailText(e.target.value)}
                    />

                    {/* ✅ Feedback: Draft Email */}
                    <Feedback
                      rfpFile={rfpFile}
                      BASE_URL={BASE_URL}
                      field_path="completeness.outreach_email"
                    />

                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      spacing={1.5}
                      sx={{ mt: 2 }}
                    >
                      <Button
                        onClick={handleApproveClick}
                        variant="contained"
                        color="primary"
                      >
                        Approve
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {/* Replacement-flow Continue button */}
              {missingItems?.length === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mb: 4,
                  }}
                >
                  <Button
                    onClick={handleContinueClick}
                    variant="contained"
                    color="primary"
                  >
                    Continue
                  </Button>
                </Box>
              )}
            </Stack>
          </>
        )}

        {/* Additional Information Dialog */}
        <Dialog
          open={additionalInfoOpen}
          onClose={handleCloseAdditionalInfo}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Additional Information</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add any extra notes or supporting documents that should be
              considered for summarization.
            </Typography>

            <Stack spacing={2}>
              {localSections?.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No additional information added yet. Use the + button below to
                  add a section.
                </Typography>
              )}

              {localSections?.map((section) => (
                <Box
                  key={section.id}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    p: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2">
                      Additional Section
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveSection(section.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>

                  <TextField
                    label="Notes / Context"
                    multiline
                    fullWidth
                    minRows={2}
                    value={section.description}
                    onChange={(e) =>
                      handleSectionTextChange(section.id, e.target.value)
                    }
                    sx={{ mb: 1.5 }}
                  />

                  <Box>
                    <input
                      type="file"
                      id={`additional-file-${section.id}`}
                      style={{ display: "none" }}
                      onChange={(e) => handleSectionFileChange(section.id, e)}
                    />
                    <label htmlFor={`additional-file-${section.id}`}>
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadFileIcon />}
                        sx={{ borderRadius: 999 }}
                      >
                        {section.file ? "Replace attachment" : "Upload file"}
                      </Button>
                    </label>
                    {section.file && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        Attached: {section.file.name}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>

            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-start" }}>
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={handleAddSection}
              >
                Add Section
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAdditionalInfo}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveAndResummarize}
              color="primary"
            >
              Save &amp; Re-summarize
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default StepOneScreen;

export const AIReviewedChip = ({ mode = "light" }) => {
  const isDark = mode === "dark";

  return (
    <Tooltip title="AI-reviewed content" arrow>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "999px",
          p: 0.5,
          bgcolor: isDark ? "rgba(255,255,255,0.14)" : "rgba(0,51,102,0.06)",
          border: isDark
            ? "1px solid rgba(255,255,255,0.5)"
            : "1px solid rgba(0,51,102,0.18)",
        }}
      >
        <AutoFixHighIcon
          sx={{
            fontSize: "1rem",
            color: isDark ? "#ffffff" : "#003366",
          }}
        />
      </Box>
    </Tooltip>
  );
};
